import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaVideo, FaPhone, FaCommentAlt, FaCheckCircle, FaTimesCircle, FaEllipsisH } from 'react-icons/fa';
import { authAPI } from '../../services/api';
import expert1 from '../../assets/team.png';

export default function AppointmentsPage() {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showDropdownId, setShowDropdownId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch appointments on component mount
  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getAppointments();
      if (response.success) {
        setAppointments(response.data);
      } else {
        setError(response.message || 'Failed to fetch appointments');
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to fetch appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    try {
      setCancellingId(appointmentToCancel.appointment_id);
      const response = await authAPI.cancelAppointment(appointmentToCancel.appointment_id);
      
      if (response.success) {
        // Update the appointment status in the local state
        setAppointments(prev => 
          prev.map(apt => 
            apt.appointment_id === appointmentToCancel.appointment_id 
              ? { ...apt, status: 'cancelled' }
              : apt
          )
        );
        setShowCancelModal(false);
        setAppointmentToCancel(null);
        // Show success message
        showSuccessAlertMessage('Appointment cancelled successfully');
      } else {
        alert(response.message || 'Failed to cancel appointment');
      }
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      alert('Failed to cancel appointment. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  const showCancelConfirmation = (appointment) => {
    setAppointmentToCancel(appointment);
    setShowCancelModal(true);
  };

  const showSuccessAlertMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage('');
    }, 4000);
  };

  // Filter appointments based on active tab
  const filteredAppointments = appointments.filter(appointment => {
    if (activeTab === 'upcoming') {
      return appointment.status === 'upcoming';
    } else if (activeTab === 'completed') {
      return appointment.status === 'completed';
    } else if (activeTab === 'cancelled') {
      return appointment.status === 'cancelled';
    }
    return true; // Show all appointments for 'all' tab
  });

  // Format date
  const formatDate = (dateString) => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Format time
  const formatTime = (timeString) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get expert image
  const getExpertImage = (expertImage) => {
    if (expertImage) {
      return expertImage.startsWith('http') ? expertImage : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${expertImage}`;
    }
    return expert1;
  };

  // Format amount
  const formatAmount = (amount) => {
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  // Toggle dropdown menu
  const toggleDropdown = (id) => {
    if (showDropdownId === id) {
      setShowDropdownId(null);
    } else {
      setShowDropdownId(id);
    }
  };

  // Get icon for appointment type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <FaVideo className="type-icon_apw" />;
      case 'voice':
        return <FaPhone className="type-icon_apw" />;
      case 'chat':
        return <FaCommentAlt className="type-icon_apw" />;
      default:
        return <FaVideo className="type-icon_apw" />;
    }
  };

  return (
    <div className="appointments-container_apw">
      <div className="appointments-header_apw">
        <h1>My Appointments</h1>
        <p>Manage your appointments with agricultural experts</p>
       
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-apw">
          <div className="success-alert-content-apw">
            <div className="success-alert-icon-apw">✓</div>
            <div className="success-alert-message-apw">{successMessage}</div>
            <button 
              className="success-alert-close-apw"
              onClick={() => setShowSuccessAlert(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="appointments-tabs_apw">
        <button 
          className={`tab-button_apw ${activeTab === 'all' ? 'active_apw' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All 
        </button>
        <button 
          className={`tab-button_apw ${activeTab === 'upcoming' ? 'active_apw' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          Upcoming
        </button>
        <button 
          className={`tab-button_apw ${activeTab === 'completed' ? 'active_apw' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed
        </button>
        <button 
          className={`tab-button_apw ${activeTab === 'cancelled' ? 'active_apw' : ''}`}
          onClick={() => setActiveTab('cancelled')}
        >
          Cancelled
        </button>
      </div>

      <div className="appointments-list_apw">
        {loading ? (
          <div className="loading-appointments_apw">
            <p>Loading appointments...</p>
          </div>
        ) : error ? (
          <div className="error-appointments_apw">
            <p>{error}</p>
            <button onClick={fetchAppointments} className="retry-button_apw">Retry</button>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="no-appointments_apw">
            <p>No {activeTab} appointments found.</p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => (
            <div 
              key={appointment.appointment_id} 
              className={`appointment-card_apw ${appointment.status === 'completed' ? 'completed_apw' : ''}`}
            >
              <div className="appointment-main_apw">
                <div className="expert-info_apw">
                  <img 
                    src={getExpertImage(appointment.expert_image)} 
                    alt={appointment.expert_name} 
                    className="expert-image_apw" 
                  />
                  <div className="expert-details_apw">
                    <h3>{appointment.expert_name}</h3>
                    <p className="specialization_apw">Agricultural Expert</p>
                  </div>
                </div>

                <div className="appointment-details_apw">
                  <div className="appointment-detail_apw">
                    <FaCalendarAlt className="detail-icon_apw" />
                    <span>{formatDate(appointment.date)}</span>
                  </div>
                  <div className="appointment-detail_apw">
                    <FaClock className="detail-icon_apw" />
                    <span>{formatTime(appointment.time)}</span>
                  </div>
                  <div className="appointment-detail_apw">
                    {getTypeIcon(appointment.consultation_type)}
                    <span className="consultation-type_apw">
                      {appointment.consultation_type.charAt(0).toUpperCase() + appointment.consultation_type.slice(1)} Consultation
                    </span>
                  </div>
                </div>

                <div className="appointment-status_apw">
                  {appointment.status === 'upcoming' ? (
                    <span className="status-badge_apw warning_apw">
                      <FaCheckCircle /> Upcoming
                    </span>
                  ) : appointment.status === 'completed' ? (
                    <span className="status-badge_apw success_apw">
                      <FaCheckCircle /> Completed
                    </span>
                  ) : (
                    <span className="status-badge_apw danger_apw">
                      <FaTimesCircle /> Cancelled
                    </span>
                  )}
                  <p className="appointment-fee_apw">Fee: {formatAmount(appointment.amount_paid)}</p>
                </div>

                <div className="appointment-actions_apw">
                  <button 
                    className="action-button_apw"
                    onClick={() => toggleDropdown(appointment.appointment_id)}
                  >
                    <FaEllipsisH />
                  </button>
                  
                  {showDropdownId === appointment.appointment_id && (
                    <div className="action-dropdown_apw">
                      {appointment.status === 'upcoming' ? (
                        <>
                          <button className="dropdown-item_apw">
                            Reschedule
                          </button>
                          <button 
                            className="dropdown-item_apw cancel_apw"
                            onClick={() => showCancelConfirmation(appointment)}
                          >
                            Cancel Appointment
                          </button>
                          <Link 
                            to={`/farmer-dashboard/experts/${appointment.expert_id}`} 
                            className="dropdown-item_apw"
                          >
                            View Expert Profile
                          </Link>
                        </>
                      ) : appointment.status === 'completed' ? (
                        <>
                          <button className="dropdown-item_apw">
                            View Session Notes
                          </button>
                          <Link 
                            to={`/farmer-dashboard/experts/${appointment.expert_id}`} 
                            className="dropdown-item_apw"
                          >
                            Book Again
                          </Link>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              <div className="appointment-topic_apw">
                <strong>Description:</strong> {appointment.description}
              </div>

              {appointment.status === 'upcoming' && (
                <div className="appointment-actions-footer_apw">
               {/*    <Link 
                    to={`/farmer-dashboard/appointments/${appointment.appointment_id}/join`} 
                    className="primary-button_apw"
                  >
                    Join Now
                  </Link> */}
                  <Link to={`/farmer-dashboard/messages?expert_id=${appointment.expert_user_id}`} className="secondary-button_apw">Contact Expert</Link>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-overlay-apw">
          <div className="modal-content-apw">
            <div className="modal-header-apw">
              <h3>Cancel Appointment</h3>
            </div>
            <div className="modal-body-apw">
              <p>Are you sure you want to cancel your appointment with <strong>{appointmentToCancel?.expert_name}</strong>?</p>
              <p className="warning-text-apw">This action cannot be undone.</p>
            </div>
            <div className="modal-footer-apw">
              <button 
                className="cancel-button-apw" 
                onClick={() => setShowCancelModal(false)}
                disabled={cancellingId}
              >
                Keep Appointment
              </button>
              <button 
                className="confirm-cancel-button-apw" 
                onClick={handleCancelAppointment}
                disabled={cancellingId}
              >
                {cancellingId ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-appointments_apw, .error-appointments_apw, .no-appointments_apw {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
        }

        .error-appointments_apw {
          color: #ef4444;
        }

        .retry-button_apw {
          background: #0e4410;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 10px;
        }

        .retry-button_apw:hover {
          background: #0a350c;
        }

        .warning_apw {
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #f59e0b;
        }

        .success_apw {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #22c55e;
        }

        .danger_apw {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #ef4444;
        }

        .modal-overlay-apw {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content-apw {
          background: white;
          border-radius: 12px;
          padding: 0;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .modal-header-apw {
          padding: 20px 20px 0 20px;
        }

        .modal-header-apw h3 {
          margin: 0;
          color: #1f2937;
          font-size: 18px;
          font-weight: 600;
        }

        .modal-body-apw {
          padding: 20px;
        }

        .modal-body-apw p {
          margin: 0 0 10px 0;
          color: #4b5563;
          line-height: 1.5;
        }

        .warning-text-apw {
          color: #ef4444;
          font-size: 14px;
          font-weight: 500;
        }

        .modal-footer-apw {
          padding: 0 20px 20px 20px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .cancel-button-apw, .confirm-cancel-button-apw {
          padding: 10px 20px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .cancel-button-apw {
          background: #f3f4f6;
          color: #374151;
        }

        .cancel-button-apw:hover {
          background: #e5e7eb;
        }

        .confirm-cancel-button-apw {
          background: #ef4444;
          color: white;
        }

        .confirm-cancel-button-apw:hover {
          background: #dc2626;
        }

        .cancel-button-apw:disabled, .confirm-cancel-button-apw:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Success Alert Styles */
        .success-alert-apw {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          animation: slideInRight 0.3s ease-out;
        }

        .success-alert-content-apw {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 300px;
        }

        .success-alert-icon-apw {
          background: #22c55e;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .success-alert-message-apw {
          color: #166534;
          font-weight: 500;
          flex: 1;
        }

        .success-alert-close-apw {
          background: none;
          border: none;
          color: #166534;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .success-alert-close-apw:hover {
          color: #0f5132;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}